/**
 * Install snippets for every SDK, shared by the dashboard's install card and the MCP server's
 * install guide so the two never drift. Zod-free, safe to import from browser bundles.
 */
import type { Platform } from "./constants";

export const INSTALL_METHODS = ["script", "npm", "reactNative", "ios", "android", "flutter", "http"] as const;
export type InstallMethod = (typeof INSTALL_METHODS)[number];

export const INSTALL_METHOD_LABEL: Record<InstallMethod, string> = {
  script: "Script tag",
  npm: "npm",
  reactNative: "React Native",
  ios: "iOS",
  android: "Android",
  flutter: "Flutter",
  http: "HTTP API",
};

/** Code language of each snippet, for highlighting. */
export const INSTALL_METHOD_LANGUAGE: Record<InstallMethod, "html" | "typescript" | "swift" | "kotlin" | "dart" | "shell"> = {
  script: "html",
  npm: "typescript",
  reactNative: "typescript",
  ios: "swift",
  android: "kotlin",
  flutter: "dart",
  http: "shell",
};

/** The usual install method for an app's platform. */
export const DEFAULT_INSTALL_METHOD: Record<Platform, InstallMethod> = {
  web: "script",
  ios: "ios",
  android: "android",
  "react-native": "reactNative",
  flutter: "flutter",
  server: "http",
};

/** The public ingest origin for a dashboard origin: app.metrickle.com → in.metrickle.com. */
export function ingestOrigin(dashboardOrigin: string): string {
  return dashboardOrigin.replace("//app.", "//in.");
}

/** Every install snippet for an app, filled in with its write key and hosts. */
export function installSnippets(app: { writeKey: string }, dashboardOrigin: string): Record<InstallMethod, string> {
  const origin = dashboardOrigin;
  const ingest = ingestOrigin(origin);
  return {
    script: `<!-- Paste before </head>. Add data-cookieless for consent-free, cookieless tracking. -->
<script defer src="${origin}/m.js" data-key="${app.writeKey}"></script>

<script>
  // Custom events & conversions. Calls wait in a queue until m.js loads.
  window.metrickle ||= { q: [], track() { this.q.push(["track", ...arguments]) } };
  metrickle.track("signup", { plan: "pro" });
  metrickle.track("purchase", { revenue: 49 });
</script>`,
    npm: `bun add @metrickle/sdk

import { init } from "@metrickle/sdk";

const metrickle = init({
  writeKey: "${app.writeKey}",
  host: "${ingest}",
  // cookieless: true,
});

metrickle.track("signup", { plan: "pro" });
metrickle.identify(user.id, { plan: user.plan }); // after login
metrickle.reset();                                // on logout

// Research (turn features on under Settings → Research)
metrickle.consent({ replay: true });  // from your consent banner
metrickle.feedback.open();            // open the feedback dialog from your own button`,
    reactNative: `bun add @metrickle/sdk @react-native-async-storage/async-storage

import AsyncStorage from "@react-native-async-storage/async-storage";
import { AppState, Dimensions, Platform } from "react-native";
import { init } from "@metrickle/sdk/react-native";

export const metrickle = init({
  writeKey: "${app.writeKey}",
  host: "${ingest}",
  storage: AsyncStorage,
  appState: AppState,
  os: Platform.OS,
  osVersion: String(Platform.Version),
  screen: Dimensions.get("screen"),
  appVersion: "1.0.0",
});

// With React Navigation: onStateChange={() => metrickle.screen(currentRouteName)}
metrickle.screen("Home");
metrickle.track("checkout_started", { items: 3 });

// Accessibility context: pass AccessibilityInfo and PixelRatio.getFontScale to init()
// Surveys: render them with your own components
metrickle.surveys.onShow((survey) => showSurveySheet(survey)); // call survey.shown(), answer(), complete()/dismiss()
// Feedback (screenshot optional, e.g. from react-native-view-shot as a data URL)
await metrickle.feedback.submit({ category: "bug", message: "Pay button does nothing" });`,
    ios: `// Xcode → File → Add Package Dependencies → https://github.com/metrickle/metrickle-swift
import Metrickle

// In your App init or application(_:didFinishLaunchingWithOptions:)
Metrickle.configure(writeKey: "${app.writeKey}", options: .init(host: "${ingest}"))

// UIKit screens are tracked automatically. In SwiftUI, name each screen:
HomeView().metrickleScreen("Home")

Metrickle.shared?.track("checkout_started", properties: ["items": 3])
Metrickle.shared?.identify("user-123", traits: ["plan": "pro"]) // after login
Metrickle.shared?.reset()                                       // on logout

// VoiceOver, Dynamic Type, Reduce Motion and more are reported automatically.
// Surveys show in an accessible native sheet; feedback from your own button:
await Metrickle.shared?.feedback.submit(category: .bug, message: "Pay button does nothing")`,
    android: `// build.gradle.kts
implementation("com.metrickle:metrickle:0.2.0")
implementation("com.metrickle:metrickle-compose:0.2.0") // Compose screens and survey sheet

// Application.onCreate()
Metrickle.init(this, "${app.writeKey}", MetrickleOptions(host = "${ingest}"))

// Activities are tracked automatically. In Compose, name each destination
// and place the survey host once at the root:
TrackScreen("Home")
MetrickleSurveyHost()

Metrickle.track("checkout_started", mapOf("items" to 3))
Metrickle.identify("user-123", mapOf("plan" to "pro")) // after login
Metrickle.reset()                                      // on logout

// TalkBack, font scale, animations off and more are reported automatically.
Metrickle.feedback.submit(FeedbackCategory.BUG, "Pay button does nothing") // from a coroutine`,
    flutter: `flutter pub add metrickle

import 'package:metrickle/metrickle.dart';

Future<void> main() async {
  WidgetsFlutterBinding.ensureInitialized();
  await Metrickle.init(
    writeKey: '${app.writeKey}',
    options: MetrickleOptions(host: '${ingest}', appVersion: '1.0.0'),
  );
  runApp(MaterialApp(
    navigatorObservers: [MetrickleNavigatorObserver()], // named routes become screens
    builder: (context, child) => MetrickleScope(child: child!), // a11y context, rage taps, surveys
    home: const HomePage(),
  ));
}

Metrickle.instance.track('checkout_started', {'items': 3});
Metrickle.instance.identify('user-123', {'plan': 'pro'}); // after login
Metrickle.instance.reset();                               // on logout`,
    http: `# Any other platform (server, game engine, desktop): POST batches of events
curl -X POST ${ingest}/v1/batch \\
  -H "content-type: application/json" \\
  -H "x-metrickle-key: ${app.writeKey}" \\
  -d '{
    "sentAt": '$(date +%s000)',
    "context": { "library": { "name": "my-sdk", "version": "1.0" }, "platform": "ios",
                 "app": { "version": "2.3.0" }, "device": { "type": "mobile", "os": "iOS" } },
    "events": [{
      "id": "'$(uuidgen | tr A-Z a-z)'", "type": "screen", "name": "$screen", "ts": '$(date +%s000)',
      "anonymousId": "device-uuid", "sessionId": "session-uuid", "path": "Onboarding/Step1"
    }]
  }'`,
  };
}

/**
 * Tying server-side revenue (Stripe, RevenueCat) to the person: give the payment provider the same
 * user id the SDK's identify() gets. One snippet per install method, for the dashboard's revenue
 * setup and the MCP install guide.
 */
export const REVENUE_SNIPPETS: Record<InstallMethod, { provider: "stripe" | "revenuecat"; language: "typescript" | "swift" | "kotlin" | "dart" | "shell"; code: string }> = {
  script: {
    provider: "stripe",
    language: "typescript",
    code: `// On your server, when you create the Checkout Session: the same id the page passes to
// metrickle.identify() (window.metrickle?.identify(user.id) after sign-in).
const session = await stripe.checkout.sessions.create({
  mode: "subscription", // or "payment"
  line_items: [{ price: "price_…", quantity: 1 }],
  client_reference_id: user.id,
  metadata: { metrickle_user_id: user.id },
  subscription_data: { metadata: { metrickle_user_id: user.id } }, // subscriptions only
  success_url: "https://example.com/welcome",
});

// Customers you create yourself: set it once and renewals and refunds follow.
await stripe.customers.update(customer.id, { metadata: { metrickle_user_id: user.id } });`,
  },
  npm: {
    provider: "stripe",
    language: "typescript",
    code: `// Client: identify the person after sign-in.
metrickle.identify(user.id);

// Server, when you create the Checkout Session: the same id.
const session = await stripe.checkout.sessions.create({
  mode: "subscription", // or "payment"
  line_items: [{ price: "price_…", quantity: 1 }],
  client_reference_id: user.id,
  metadata: { metrickle_user_id: user.id },
  subscription_data: { metadata: { metrickle_user_id: user.id } }, // subscriptions only
  success_url: "https://example.com/welcome",
});

// Without Checkout (Payment Element): put it on the PaymentIntent, or on the customer once.
await stripe.paymentIntents.create({ amount: 4900, currency: "usd", customer: customer.id, metadata: { metrickle_user_id: user.id } });
await stripe.customers.update(customer.id, { metadata: { metrickle_user_id: user.id } });`,
  },
  reactNative: {
    provider: "revenuecat",
    language: "typescript",
    code: `import Purchases from "react-native-purchases";

// After sign-in, the same id for both:
metrickle.identify(user.id);
await Purchases.logIn(user.id);

// If RevenueCat's app user id has to stay something else, name the Metrickle user instead:
// Purchases.setAttributes({ metrickle_user_id: user.id });`,
  },
  ios: {
    provider: "revenuecat",
    language: "swift",
    code: `import RevenueCat

// After sign-in, the same id for both:
Metrickle.shared?.identify(user.id)
_ = try await Purchases.shared.logIn(user.id)

// If RevenueCat's app user id has to stay something else, name the Metrickle user instead:
// Purchases.shared.attribution.setAttributes(["metrickle_user_id": user.id])`,
  },
  android: {
    provider: "revenuecat",
    language: "kotlin",
    code: `import com.revenuecat.purchases.Purchases
import com.revenuecat.purchases.logInWith

// After sign-in, the same id for both:
Metrickle.identify(user.id)
Purchases.sharedInstance.logInWith(user.id)

// If RevenueCat's app user id has to stay something else, name the Metrickle user instead:
// Purchases.sharedInstance.setAttributes(mapOf("metrickle_user_id" to user.id))`,
  },
  flutter: {
    provider: "revenuecat",
    language: "dart",
    code: `import 'package:purchases_flutter/purchases_flutter.dart';

// After sign-in, the same id for both:
Metrickle.instance.identify(user.id);
await Purchases.logIn(user.id);

// If RevenueCat's app user id has to stay something else, name the Metrickle user instead:
// await Purchases.setAttributes({'metrickle_user_id': user.id});`,
  },
  http: {
    provider: "stripe",
    language: "shell",
    code: `# Send events with "userId" set to your user's id, and give their Stripe customer the same id:
curl https://api.stripe.com/v1/customers/cus_… \\
  -H "Authorization: Bearer $STRIPE_SECRET_KEY" \\
  -d "metadata[metrickle_user_id]=user-123"

# Checkout Sessions: client_reference_id=user-123 (or metadata[metrickle_user_id]=user-123)`,
  },
};
